import { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui';
import { Skeleton } from '@/components/custom';
import { useRequestGetPromoCodeStats, useRequestGetPromoCodeUsages } from '@/hooks/httpRequest/promoCodes';
import type { IPromoCodeOffer } from '@/interfaces';

type TProps = {
	offer: IPromoCodeOffer | null;
	onClose: () => void;
};

const PromoCodeStatsDialog = ({ offer, onClose }: TProps) => {
	// the usages of the offer are paged here, not in the URL: the list behind keeps its own page
	const [page, setPage] = useState(1);
	const { stats, isLoading } = useRequestGetPromoCodeStats(offer?._id);
	const { usages, isLoading: isLoadingUsages } = useRequestGetPromoCodeUsages(offer?._id, page);

	const tiles = stats
		? [
				{ label: 'Utilisations', value: stats.uses.toLocaleString('fr-FR') },
				{ label: 'Utilisateurs uniques', value: stats.uniqueUsers.toLocaleString('fr-FR') },
				{ label: 'Pièces offertes', value: stats.bonusCoins.toLocaleString('fr-FR') },
				{ label: 'Revenu généré', value: `${stats.revenueEur.toLocaleString('fr-FR')} €` },
				{ label: 'Codes appliqués', value: stats.applications.toLocaleString('fr-FR') },
				{ label: 'Conversion', value: `${stats.conversionRate}%` },
			]
		: [];

	return (
		<Dialog open={!!offer} onOpenChange={value => !value && onClose()}>
			<DialogContent className="max-w-2xl w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto rounded-2xl">
				<DialogHeader>
					<DialogTitle>
						{offer?.name} <span className="font-mono text-muted-foreground">({offer?.code})</span>
					</DialogTitle>
				</DialogHeader>

				<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
					{isLoading
						? Array.from({ length: 6 }, (_, index) => (
								<div key={`tile-${index}`} className="rounded-lg border p-3 space-y-2">
									<Skeleton className="h-3 w-24" />
									<Skeleton className="h-6 w-16" />
								</div>
							))
						: tiles.map(tile => (
								<div key={tile.label} className="rounded-lg border p-3">
									<p className="text-xs text-muted-foreground">{tile.label}</p>
									<p className="text-xl font-semibold">{tile.value}</p>
								</div>
							))}
				</div>

				{stats && stats.perDay.length > 0 && (
					<div className="space-y-1">
						<p className="text-sm font-medium">Utilisations par jour</p>
						<div className="flex flex-wrap gap-2 text-xs">
							{stats.perDay.map(day => (
								<span key={day.date} className="rounded-md bg-muted px-2 py-1">
									{format(new Date(day.date), 'dd MMM', { locale: fr })}: {day.uses}
								</span>
							))}
						</div>
					</div>
				)}

				<div className="space-y-2">
					<p className="text-sm font-medium">Dernières utilisations</p>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Utilisateur</TableHead>
								<TableHead>Pack</TableHead>
								<TableHead>Pièces offertes</TableHead>
								<TableHead>Montant</TableHead>
								<TableHead>Date</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoadingUsages
								? Array.from({ length: 5 }, (_, index) => (
										<TableRow key={`usage-${index}`}>
											{Array.from({ length: 5 }, (_cell, cellIndex) => (
												<TableCell key={`usage-${index}-${cellIndex}`}>
													<Skeleton className="h-4 w-20" />
												</TableCell>
											))}
										</TableRow>
									))
								: (usages?.data ?? []).map(usage => (
										<TableRow key={usage._id}>
											<TableCell>{usage.username ?? usage.userId}</TableCell>
											<TableCell>{usage.packageName}</TableCell>
											<TableCell>{usage.bonusCoins.toLocaleString('fr-FR')}</TableCell>
											<TableCell>{usage.priceInEur !== undefined ? `${usage.priceInEur} €` : '-'}</TableCell>
											<TableCell className="text-xs">{format(new Date(usage.createdAt), 'dd MMM yyyy HH:mm', { locale: fr })}</TableCell>
										</TableRow>
									))}
						</TableBody>
					</Table>
					{!isLoadingUsages && (usages?.data ?? []).length === 0 && (
						<p className="text-center text-sm text-muted-foreground py-4">Aucune utilisation pour le moment.</p>
					)}
					{usages?.pagination && usages.pagination.pageCount > 1 && (
						<div className="flex items-center justify-between">
							<Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
								Précédent
							</Button>
							<span className="text-xs text-muted-foreground">
								{page} sur {usages.pagination.pageCount}
							</span>
							<Button size="sm" variant="outline" disabled={page >= usages.pagination.pageCount} onClick={() => setPage(page + 1)}>
								Suivant
							</Button>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PromoCodeStatsDialog;
